import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Team, TeamFilters } from "../../types/team";
import EntityTable from "../EntityTable";
import { renderRowViewAndUpdateActions } from "../../common/TableUtils";
import {
  extractTeamEditLink,
  extractTeamViewLink,
} from "../entitiesReferences/InternalEntityLink";
import { MRT_ColumnDef } from "material-react-table";
import useTeamQuery from "../../hooks/query/useTeamQuery";
import useTeamLogic from "../../hooks/logic/teamLogic";
import useServerFilters from "../../hooks/useServerFilters";
import { teamFilterSpec } from "../../common/entities/TeamColumns";

interface TeamTableProps {
  columns?: MRT_ColumnDef<Team, any>[];
  filters: TeamFilters;
  enableRowActions?: boolean;
}

const TeamTable: React.FC<TeamTableProps> = ({
  columns,
  filters,
  enableRowActions = true,
}) => {
  const {
    serverFilters,
    entityTableFilterProps,
    pagination,
    setPagination,
  } = useServerFilters(filters, teamFilterSpec);
  const { useFetchAll } = useTeamQuery(filters.system_language_type);
  const { languageBasedColumns } = useTeamLogic(filters.system_language_type);
  const { data, isLoading, errorMessage } = useFetchAll({
    filters: serverFilters,
    externalPagination: pagination,
    externalSetPagination: setPagination,
  });

  const navigate = useNavigate();

  const renderRowActions = useMemo(
    () =>
      renderRowViewAndUpdateActions(
        (team: Team) => navigate(extractTeamViewLink(team)),
        (team: Team) => navigate(extractTeamEditLink(team))
      ),
    [navigate]
  );

  return (
    <EntityTable
      loading={isLoading}
      error={errorMessage}
      columns={columns || languageBasedColumns}
      renderRowActions={renderRowActions}
      enableRowActions={enableRowActions}
      data={data?.data || []}
      pagination={pagination}
      manualPagination={true}
      onPaginationChange={setPagination}
      totalRowCount={data?.total || 0}
      manualFiltering
      {...entityTableFilterProps}
    />
  );
};

export default TeamTable;
